import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";
import { AlertTriangle, Check, Info, X } from "lucide-react";

type ToastType = "success" | "error" | "info";
type Toast = { id: number; message: string; type: ToastType };

const ToastContext = createContext<(message: string, type?: ToastType) => void>(
  () => undefined,
);

/** Fire a toast from anywhere: `const toast = useToast(); toast("Guardado")`. */
export const useToast = () => useContext(ToastContext);

let counter = 0;

/**
 * Global brutalist toast stack (bottom-right). Success/info auto-dismiss; errors
 * stay until dismissed so a failure is never missed. Portalled over everything,
 * so a save/error notice is always visible without scrolling to the top.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback(
    (id: number) => setToasts((list) => list.filter((t) => t.id !== id)),
    [],
  );
  const toast = useCallback(
    (message: string, type: ToastType = "success") => {
      const id = (counter += 1);
      setToasts((list) => [...list, { id, message, type }]);
      if (type !== "error") window.setTimeout(() => dismiss(id), 4000);
    },
    [dismiss],
  );
  return (
    <ToastContext.Provider value={toast}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-[min(92vw,360px)] flex-col gap-2">
          <AnimatePresence>
            {toasts.map((t) => (
              <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
            ))}
          </AnimatePresence>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: () => void;
}) {
  const tone =
    toast.type === "error"
      ? "bg-bubble"
      : toast.type === "info"
        ? "bg-lemon"
        : "bg-acid";
  const Icon =
    toast.type === "error"
      ? AlertTriangle
      : toast.type === "info"
        ? Info
        : Check;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 48 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 48 }}
      transition={{ type: "spring", stiffness: 400, damping: 32 }}
      role={toast.type === "error" ? "alert" : "status"}
      className={`border-ink shadow-brutal text-ink border-surface pointer-events-auto flex items-start gap-2.5 p-3 ${tone}`}
    >
      <span className="border-ink bg-bone border-chip grid h-6 w-6 shrink-0 place-items-center">
        <Icon size={14} />
      </span>
      <p className="font-display flex-1 pt-0.5 text-sm font-bold leading-snug">
        {toast.message}
      </p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Cerrar"
        className="shrink-0 transition-opacity hover:opacity-60"
      >
        <X size={16} />
      </button>
    </motion.div>
  );
}
